import { cn } from '@/lib/utils';

interface InsightsHudBarProps {
  ctl: number;
  atl: number;
  tsb: number;
  riskBand?: 'normal' | 'elevated' | 'high' | 'critical';
  className?: string;
}

/**
 * System Diagnostic HUD bar — fixed at top of Insights page.
 * Shows SYS status, TSB (Freshness Index), and risk level in monospace.
 *
 * TASK-021: Label changed from "RDY:" to "TSB:" with sign convention.
 * Positive = fresh, negative = fatigued. Consistent with all other surfaces.
 */
export function InsightsHudBar({ ctl, atl, tsb, riskBand = 'normal', className }: InsightsHudBarProps) {
  const riskLabel = riskBand.toUpperCase();
  const riskColor = riskBand === 'critical' || riskBand === 'high'
    ? 'text-[hsl(35,85%,55%)]'
    : riskBand === 'elevated'
      ? 'text-[hsl(35,85%,55%)]'
      : 'text-[hsl(170,80%,45%)]';

  // Match InsightsPulseChart "Load Balance" metric
  const loadBalancePct = atl > 0 ? (ctl / atl) * 100 : 0;
  const loadBalanceLabel = `${Math.max(0, Math.min(999, Math.round(loadBalancePct)))}%`;
  const readinessLabel = tsb > 5
    ? 'Legs look fresh'
    : tsb < -15
      ? 'Keep it controlled'
      : 'Steady and durable';

  return (
    <div className={cn(
      'flex items-center gap-4 sm:gap-6 px-4 py-2.5 rounded-lg font-mono text-[10px] tracking-[0.15em] uppercase',
      'border border-white/[0.08]',
      'bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className
    )}>
      {/* System status */}
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[hsl(170,80%,45%)] shadow-[0_0_6px_rgba(0,255,200,0.6)] animate-pulse" />
        <span className="text-[hsl(170,80%,45%)]">SYS: ACTIVE</span>
      </div>

      <div className="w-px h-3 bg-white/10" />

      {/* TSB / Freshness Index */}
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">TSB:</span>
        <span className={
          tsb > 5 ? 'text-green-400' : tsb > -10 ? 'text-[hsl(170,80%,45%)]' : 'text-red-400'
        }>
          {Number.isFinite(tsb) ? `${tsb > 0 ? '+' : ''}${tsb.toFixed(1)}` : '—'}
        </span>
        <span className="hidden md:inline text-muted-foreground">{readinessLabel}</span>
      </div>

      <div className="w-px h-3 bg-white/10" />

      {/* Risk */}
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">RISK:</span>
        <span className={riskColor}>{riskLabel}</span>
      </div>

      <div className="w-px h-3 bg-white/10 hidden sm:block" />

      {/* Load ratio */}
      <div className="hidden sm:flex items-center gap-2">
        <span className="text-muted-foreground">LOAD:</span>
        <span className="text-foreground">{loadBalanceLabel}</span>
      </div>
    </div>
  );
}
